import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Coins, Clock, WarningOctagon } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'autospa5-sub-expiry-dismissed';

/**
 * Pop-up that warns the customer when their subscription is about to expire (≤3 days)
 * or has already expired. Offers cash + card payment options to renew.
 * Renders ONLY for logged-in customers (not staff). One pop-up per session.
 */
export default function SubscriptionExpiryGuard() {
  const { user } = useAuth();
  const { t, fmtDate } = useLanguage();
  const [activeSub, setActiveSub] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user || user.role !== 'user') return;
    try {
      const res = await axios.get(`${API}/subscriptions/my`, { withCredentials: true });
      const subs = res.data || [];
      // Pick the most recent subscription (active or expired)
      const sorted = [...subs].sort((a, b) => new Date(b.expires_at || 0) - new Date(a.expires_at || 0));
      const latest = sorted[0];
      if (!latest) return;
      const exp = new Date(latest.expires_at);
      const now = new Date();
      const msPerDay = 24 * 60 * 60 * 1000;
      const daysLeft = Math.ceil((exp - now) / msPerDay);
      // Dismiss key includes subscription id so each renewal can re-prompt
      const dismissedKey = `${DISMISS_KEY}-${latest.subscription_id}`;
      const dismissed = sessionStorage.getItem(dismissedKey) === '1';
      if (!dismissed && (daysLeft <= 0 || daysLeft <= 3)) {
        setActiveSub({ ...latest, daysLeft });
        setOpen(true);
      }
    } catch (err) { if (err?.response?.status !== 401) console.warn('[ExpiryGuard] load failed', err?.response?.status || err?.message); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const dismiss = () => {
    if (activeSub) {
      try { sessionStorage.setItem(`${DISMISS_KEY}-${activeSub.subscription_id}`, '1'); } catch (err) { console.debug('[ExpiryGuard] sessionStorage unavailable', err?.message); }
    }
    setOpen(false);
  };

  if (!activeSub) return null;
  const expired = activeSub.daysLeft <= 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-auto" data-testid="sub-expiry-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {expired
              ? <WarningOctagon size={20} weight="fill" className="text-red-400" />
              : <Clock size={20} weight="fill" className="text-[#FF5A00]" />}
            {expired ? t('subExpiredTitle') : t('subExpiringSoonTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-white/70 text-sm">
            {expired ? t('subExpiredBody') : t('subExpiringSoonBody')}
          </p>
          <div className="p-3 rounded-xl bg-white/5 border border-white/5">
            <p className="text-xs text-white/45 uppercase tracking-wider">{activeSub.plan_name}</p>
            <p className="text-sm text-white mt-1">
              {expired ? t('expired') : t('daysLeft').replace('{n}', String(activeSub.daysLeft))}
            </p>
            <p className="text-[10px] text-white/40 mt-0.5">
              {t('subValidFrom')}: {fmtDate(activeSub.created_at)} · {t('subValidTo')}: {fmtDate(activeSub.expires_at)}
            </p>
          </div>

          {/* Cash + Card renew options */}
          <Button
            onClick={() => { window.location.href = '/subscriptions'; }}
            className="cta-button-orange w-full h-12 rounded-xl text-sm font-semibold"
            data-testid="sub-expiry-cash"
          >
            <Coins size={16} className="mr-2" weight="fill" /> {t('payNowCash')}
          </Button>
          <button
            onClick={dismiss}
            className="w-full h-10 text-xs text-white/45 hover:text-white/80 transition"
            data-testid="sub-expiry-dismiss"
          >
            {t('notNow')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
